import { useCallback, useEffect, useRef, useState } from "react";
import type { CardView } from "../api/types";
import type { Theme } from "../theme/pixel";
import { Card, naturalCardWidthPx } from "./Card";
import { E32 } from "../theme/endesga32";
import { PickOrderBadge } from "./PickOrderBadge";
import { useClickOutsideCards } from "../logic/useClickOutsideCards";
import { playSfx } from "../logic/sfx";
import { ghostBtn, primaryBtn } from "../theme/styles";
import { useModalFocus } from "../logic/useModalFocus";

// Modal viewer for one zone of cards (deck/graveyard/exile/reveal).
// When `targeting` is provided, clicking a candidate toggles the pick
// in addition to inspecting; non-candidates are inspect-only.
export interface ZoneViewerTargeting {
  candidateEntities: Set<number>;
  /** eid → 1-based pick order. */
  pickedEntities: Map<number, number>;
  min: number;
  max: number;
  prompt: string | null;
  onTogglePick: (eid: number) => void;
  onConfirm: () => void;
  onClear: () => void;
}

export interface ZoneViewerProps {
  zone: "deck" | "graveyard" | "exile" | "opp_graveyard" | "opp_exile" | "reveal";
  cards: CardView[];
  theme: Theme;
  onClose: () => void;
  targeting?: ZoneViewerTargeting;
}

export function ZoneViewer({
  zone,
  cards,
  theme,
  onClose,
  targeting,
}: ZoneViewerProps) {
  const [inspectedEid, setInspectedEid] = useState<number | null>(
    cards.length > 0 ? cards[0].entity_id : null,
  );

  // Refocus on first card if the inspected one disappears mid-modal.
  useEffect(() => {
    if (
      inspectedEid !== null &&
      !cards.some((c) => c.entity_id === inspectedEid)
    ) {
      setInspectedEid(cards.length > 0 ? cards[0].entity_id : null);
    }
  }, [cards, inspectedEid]);

  const dialogRef = useRef<HTMLDivElement>(null);
  useModalFocus(dialogRef, onClose);

  // While targeting, skip click-outside so Confirm/Clear stay clickable
  // without dropping the inspect.
  const clearInspect = useCallback(() => setInspectedEid(null), []);
  useClickOutsideCards(
    inspectedEid !== null && targeting === undefined,
    clearInspect,
  );

  const zoneLabel =
    zone === "deck"
      ? "Deck"
      : zone === "graveyard"
        ? "Graveyard"
        : zone === "exile"
          ? "Exile"
          : zone === "opp_graveyard"
            ? "Opponent's Graveyard"
            : zone === "opp_exile"
              ? "Opponent's Exile"
              : "Revealed";

  const inspected = cards.find((c) => c.entity_id === inspectedEid) ?? null;
  const pickCount = targeting?.pickedEntities.size ?? 0;
  const ready =
    targeting !== undefined &&
    pickCount >= targeting.min &&
    pickCount <= targeting.max;

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-label={`${zoneLabel} viewer`}
      tabIndex={-1}
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(10, 8, 4, 0.82)",
        zIndex: 1000,
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        justifyContent: "stretch",
        padding: 24,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          flex: 1,
          minHeight: 0,
          display: "grid",
          gridTemplateRows: "auto 1fr",
          gap: 12,
          background: theme.panel,
          border: `3px solid ${theme.panelEdge}`,
          color: theme.ink,
          fontFamily: theme.fontBody,
          padding: 16,
        }}
      >
        <Header
          theme={theme}
          zoneLabel={zoneLabel}
          count={cards.length}
          targeting={targeting}
          ready={ready}
          pickCount={pickCount}
          onClose={onClose}
        />

        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 320px",
            gap: 12,
            minHeight: 0,
          }}
        >
          <div
            style={{
              overflowY: "auto",
              border: `2px solid ${theme.panelEdge}`,
              padding: 12,
              background: theme.bgDeep,
            }}
          >
            {cards.length === 0 ? (
              <div
                style={{
                  color: theme.inkFaint,
                  fontFamily: theme.fontDisplay,
                  fontSize: 11,
                  letterSpacing: theme.letterSpacing,
                  textAlign: "center",
                  padding: 32,
                }}
              >
                {zoneLabel.toUpperCase()} IS EMPTY
              </div>
            ) : (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: `repeat(auto-fill, ${naturalCardWidthPx("small")}px)`,
                  gap: 12,
                  justifyContent: "center",
                }}
              >
                {cards.map((c) => {
                  const isCandidate =
                    targeting?.candidateEntities.has(c.entity_id) === true;
                  const pickOrder = targeting?.pickedEntities.get(c.entity_id);
                  const isPicked = pickOrder !== undefined;
                  const isInspected = c.entity_id === inspectedEid;
                  const showOrder =
                    pickOrder !== undefined &&
                    (targeting?.pickedEntities.size ?? 0) > 1;
                  return (
                    <div
                      key={c.entity_id}
                      style={{ position: "relative", width: naturalCardWidthPx("small") }}
                      data-card-tile="true"
                    >
                      <Card
                        card={c}
                        size="small"
                        theme={theme}
                        selected={isPicked || isInspected}
                        ringColor={
                          isPicked
                            ? theme.accent
                            : isCandidate
                              ? E32.amber
                              : undefined
                        }
                        onClick={() => {
                          setInspectedEid(c.entity_id);
                          if (targeting) {
                            if (isCandidate) {
                              targeting.onTogglePick(c.entity_id);
                            } else {
                              playSfx("ai_notice_or_warning_sound");
                            }
                          }
                        }}
                      />
                      {showOrder && pickOrder !== undefined && (
                        <PickOrderBadge order={pickOrder} theme={theme} />
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <DetailPanel theme={theme} card={inspected} />
        </div>
      </div>
    </div>
  );
}

function Header({
  theme,
  zoneLabel,
  count,
  targeting,
  ready,
  pickCount,
  onClose,
}: {
  theme: Theme;
  zoneLabel: string;
  count: number;
  targeting?: ZoneViewerTargeting;
  ready: boolean;
  pickCount: number;
  onClose: () => void;
}) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 14,
        flexWrap: "wrap",
      }}
    >
      <div
        style={{
          fontFamily: theme.fontDisplay,
          fontSize: 16,
          letterSpacing: theme.letterSpacing,
          color: theme.accent,
        }}
      >
        {zoneLabel.toUpperCase()}
      </div>
      <div
        style={{
          fontFamily: theme.fontDisplay,
          fontSize: 10,
          letterSpacing: theme.letterSpacing,
          color: theme.inkDim,
        }}
      >
        {count} CARD{count === 1 ? "" : "S"}
      </div>

      {targeting && (
        <>
          <div
            style={{
              flex: 1,
              minWidth: 200,
              fontFamily: theme.fontBody,
              fontSize: 13,
              color: theme.ink,
              borderLeft: `2px solid ${theme.panelEdge}`,
              paddingLeft: 12,
            }}
          >
            {targeting.prompt ?? "Choose targets"}
            <span
              style={{
                marginLeft: 10,
                fontFamily: theme.fontDisplay,
                fontSize: 10,
                color: theme.inkDim,
              }}
            >
              {pickCount} /{" "}
              {targeting.min === targeting.max
                ? targeting.max
                : `${targeting.min}-${targeting.max}`}
            </span>
          </div>
          <button
            onClick={targeting.onClear}
            disabled={pickCount === 0}
            {...ghostButton(theme, pickCount === 0)}
          >
            CLEAR
          </button>
          <button
            onClick={() => {
              targeting.onConfirm();
              onClose();
            }}
            disabled={!ready}
            {...primaryButton(theme, !ready)}
          >
            CONFIRM
          </button>
        </>
      )}

      <div style={{ flex: targeting ? undefined : 1 }} />

      <button onClick={onClose} {...ghostButton(theme, false)}>
        CLOSE (ESC)
      </button>
    </div>
  );
}

function DetailPanel({ theme, card }: { theme: Theme; card: CardView | null }) {
  if (!card) {
    return (
      <div
        style={{
          border: `2px solid ${theme.panelEdge}`,
          background: theme.bgDeep,
          padding: 16,
          color: theme.inkFaint,
          fontFamily: theme.fontDisplay,
          fontSize: 10,
          letterSpacing: theme.letterSpacing,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        SELECT A CARD
      </div>
    );
  }
  return (
    <div
      style={{
        border: `2px solid ${theme.panelEdge}`,
        background: theme.bgDeep,
        padding: 16,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        overflowY: "auto",
      }}
    >
      <div style={{ width: naturalCardWidthPx("hero") }}>
        <Card card={card} size="hero" theme={theme} />
      </div>
    </div>
  );
}

const ghostButton = ghostBtn;
const primaryButton = primaryBtn;
